import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useCostPermissions } from '../hooks/useRBAC'; // Use centralized RBAC hook
import { useUserStore } from '../stores/useUserStore';
import { normalizeVietnamese } from '../utils/normalizeVietnamese';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  FileText,
  Calendar,
  DollarSign,
  Tag,
  ChevronRight,
  ChevronDown,
  Grid3x3,
  ListTree,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import CostService from '../services/costService';
import { nekoPos } from 'public-contracts';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

type CostCategory = nekoPos.CostCategory;

const ModernCostCategoryList: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { canDelete, canEdit, canCreate } = useCostPermissions(); // Use centralized RBAC hook
  const { user, fetchUser } = useUserStore();
  const costService = useMemo(() => new CostService(), []);

  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [allCategories, setAllCategories] = useState<CostCategory[]>([]); // All categories for parent lookup
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Load view mode from localStorage, default to 'table'
  const [viewMode, setViewMode] = useState<'table' | 'tree'>(() => {
    const saved = localStorage.getItem('costCategoryViewMode');
    return (saved === 'table' || saved === 'tree') ? saved : 'table';
  });
  
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('costCategoryViewMode', viewMode);
  }, [viewMode]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load all categories for parent lookup and tree view
  useEffect(() => {
    const loadAllCategories = async () => {
      try {
        const allCategoriesData = await costService.getCostCategories({
          perPage: 1000, // Get all categories for lookup
        });
        const allCategoriesArray = Array.isArray(allCategoriesData)
          ? allCategoriesData
          : [];
        setAllCategories(allCategoriesArray);
        
        // Auto-expand all nodes when switching to tree view
        if (viewMode === 'tree') {
          setExpandedNodes(new Set(allCategoriesArray.map((cat) => cat.id)));
        }
      } catch (error) {
        console.error('Error loading all categories for lookup:', error);
        setAllCategories([]);
      }
    };
    loadAllCategories();
  }, [costService, viewMode]);

  // Debounce search and reset to page 1 when search changes
  useEffect(() => {
    if (searchTerm && pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchTerm]);

  // Initial load - load all categories for grouping
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Load all categories for grouping (we'll paginate on client side)
        const categoriesData = await costService.getCostCategories({
          perPage: 1000, // Get all categories
        });

        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];
        const total = (categoriesData as any).__total || categoriesArray.length;

        setCategories(categoriesArray);
        setPagination((prev) => ({ ...prev, total }));
        setError(null);
      } catch (error) {
        console.error('Error loading cost category data:', error);
        setError(translate('pos.common.failedToLoadCostCategories'));
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Create Vietnamese-accent-insensitive regex pattern
  const createVietnameseRegex = (searchTerm: string): string => {
    if (!searchTerm) return '';

    // Normalize the search term to remove accents for matching
    const normalized = normalizeVietnamese(searchTerm);

    // Escape special regex characters
    const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a pattern that matches Vietnamese character variations
    // Map each character to its Vietnamese variations
    const vietnameseMap: { [key: string]: string } = {
      a: '[aàáạảãâầấậẩẫăằắặẳẵAÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴ]',
      e: '[eèéẹẻẽêềếệểễEÈÉẸẺẼÊỀẾỆỂỄ]',
      i: '[iìíịỉĩIÌÍỊỈĨ]',
      o: '[oòóọỏõôồốộổỗơờớợởỡOÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠ]',
      u: '[uùúụủũưừứựửữUÙÚỤỦŨƯỪỨỰỬỮ]',
      y: '[yỳýỵỷỹYỲÝỴỶỸ]',
      d: '[dđDĐ]',
    };

    // Build regex pattern character by character
    let pattern = '';
    for (const char of escaped) {
      const lowerChar = char.toLowerCase();
      if (vietnameseMap[lowerChar]) {
        pattern += vietnameseMap[lowerChar];
      } else {
        pattern += char;
      }
    }

    return pattern;
  };

  // Create a map of category IDs to names for parent lookup
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    allCategories.forEach((cat) => {
      map.set(cat.id, cat.name);
    });
    return map;
  }, [allCategories]);

  // Group and sort ALL categories by parent (use allCategories for complete grouping)
  const allGroupedCategories = useMemo(() => {
    // Group categories by parent ID
    const groups = new Map<string | null, CostCategory[]>();

    allCategories.forEach((category) => {
      const parentId = category.parent || null;
      if (!groups.has(parentId)) {
        groups.set(parentId, []);
      }
      groups.get(parentId)!.push(category);
    });

    // Sort categories within each group by name
    groups.forEach((groupCategories) => {
      groupCategories.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Convert to array and sort groups by parent name
    // Categories without parent come first, then sorted by parent name
    const groupedArray = Array.from(groups.entries()).map(
      ([parentId, cats]) => {
        const parentName = parentId
          ? categoryMap.get(parentId) || parentId
          : null;
        return {
          parentId,
          parentName,
          categories: cats,
        };
      }
    );

    // Sort groups: no parent first, then by parent name
    groupedArray.sort((a, b) => {
      if (a.parentId === null && b.parentId !== null) return -1;
      if (a.parentId !== null && b.parentId === null) return 1;
      if (a.parentId === null && b.parentId === null) return 0;
      // Both have parents, sort by parent name
      const aName = a.parentName || '';
      const bName = b.parentName || '';
      return aName.localeCompare(bName);
    });

    return groupedArray;
  }, [allCategories, categoryMap]);

  // Filter categories by search term (client-side filtering after grouping)
  const filteredGroupedCategories = useMemo(() => {
    if (!searchTerm) {
      return allGroupedCategories;
    }

    const regexPattern = createVietnameseRegex(searchTerm);
    const regex = new RegExp(regexPattern, 'i');

    // Filter groups and categories within groups
    return allGroupedCategories
      .map((group) => {
        // Filter categories in this group
        const filteredCategories = group.categories.filter(
          (category) =>
            regex.test(category.name) || regex.test(category.description || '')
        );

        // Include group if it has matching categories or if parent name matches
        const parentMatches = group.parentName && regex.test(group.parentName);
        if (filteredCategories.length > 0 || parentMatches) {
          return {
            ...group,
            categories: parentMatches ? group.categories : filteredCategories, // Show all if parent matches, otherwise only filtered
          };
        }
        return null;
      })
      .filter(
        (group): group is (typeof allGroupedCategories)[0] => group !== null
      );
  }, [allGroupedCategories, searchTerm]);

  // Calculate pagination for grouped categories
  // We paginate by groups, ensuring complete groups are shown on each page
  const paginatedGroupedCategories = useMemo(() => {
    // Calculate total items (groups + all categories in those groups)
    const totalItems = filteredGroupedCategories.reduce(
      (sum, group) => sum + 1 + group.categories.length, // +1 for group header
      0
    );

    // Calculate which page each group belongs to
    let currentPage = 1;
    let currentItemCount = 0;
    const groupsByPage: (typeof filteredGroupedCategories)[] = [];
    let currentPageGroups: typeof filteredGroupedCategories = [];

    for (const group of filteredGroupedCategories) {
      const groupSize = 1 + group.categories.length; // header + categories

      // If this is the first group and it's too large, put it on page 1 anyway
      if (currentItemCount === 0 && groupsByPage.length === 0) {
        currentPageGroups.push(group);
        currentItemCount += groupSize;
      } else if (currentItemCount + groupSize <= pagination.perPage) {
        // Group fits on current page
        currentPageGroups.push(group);
        currentItemCount += groupSize;
      } else {
        // Group doesn't fit on current page
        // Save current page and start a new page
        if (currentPageGroups.length > 0) {
          groupsByPage.push(currentPageGroups);
        }
        currentPage++;
        currentPageGroups = [group];
        currentItemCount = groupSize;
      }
    }

    // Don't forget to add the last page
    if (currentPageGroups.length > 0) {
      groupsByPage.push(currentPageGroups);
    }

    // Get groups for the current page (page is 1-indexed)
    const currentPageIndex = pagination.page - 1;
    const result = groupsByPage[currentPageIndex] || [];

    return {
      groups: result,
      totalItems,
      totalGroups: filteredGroupedCategories.length,
      totalPages: groupsByPage.length,
    };
  }, [filteredGroupedCategories, pagination.page, pagination.perPage]);

  // Use grouped categories for display
  const groupedCategories = paginatedGroupedCategories.groups;

  // Build tree structure from flat category list for tree view
  const categoryTree = useMemo(() => {
    if (viewMode !== 'tree') return [];

    const categoryMap = new Map<string, CostCategory & { children: CostCategory[] }>();
    const rootCategories: (CostCategory & { children: CostCategory[] })[] = [];

    // First pass: create map with children arrays
    allCategories.forEach((category) => {
      categoryMap.set(category.id, { ...category, children: [] });
    });

    // Second pass: build tree structure
    allCategories.forEach((category) => {
      const categoryWithChildren = categoryMap.get(category.id)!;
      if (category.parent && categoryMap.has(category.parent)) {
        const parent = categoryMap.get(category.parent)!;
        parent.children.push(categoryWithChildren);
      } else {
        rootCategories.push(categoryWithChildren);
      }
    });

    // Sort by name
    const sortCategories = (
      cats: (CostCategory & { children: CostCategory[] })[]
    ) => {
      cats.sort((a, b) => a.name.localeCompare(b.name));
      cats.forEach((cat) => {
        if (cat.children.length > 0) {
          sortCategories(cat.children as (CostCategory & { children: CostCategory[] })[]);
        }
      });
    };

    sortCategories(rootCategories);
    return rootCategories;
  }, [allCategories, viewMode]);

  // Toggle node expansion
  const toggleNode = (categoryId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const canList = canCreate; // Use centralized RBAC hook
  const canEditCategory = canEdit;
  const canDeleteCategory = canDelete;

  console.log('Permission results:', {
    canList,
    canCreate,
    canEditCategory,
    canDeleteCategory,
  });

  if (!canList) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.accessDenied')}
          </p>
          <p className="text-muted-foreground">
            {translate('pos.common.noPermissionCostCategoryManagement')}
          </p>
        </div>
      </div>
    );
  }

  // Helper function to get parent category name
  const getParentCategoryName = (
    parentId: string | null | undefined
  ): string => {
    if (!parentId) return '-';
    return categoryMap.get(parentId) || parentId; // Fallback to ID if not found
  };

  const handleCreateCategory = () => {
    navigate('/cost_categories/create');
  };

  const handleEditCategory = (id: string) => {
    navigate(`/cost_categories/${id}/edit`);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!canDeleteCategory) {
      alert(translate('pos.cost_categories.no_permission_delete'));
      return;
    }

    if (window.confirm(translate('pos.cost_categories.delete_confirm'))) {
      try {
        await costService.deleteCostCategory(id);
        // Remove from both categories and allCategories
        setCategories(categories.filter((cat) => cat.id !== id));
        setAllCategories(allCategories.filter((cat) => cat.id !== id));
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(translate('pos.cost_categories.delete_error'));
      }
    }
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';
      return dateObj.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            {translate('pos.common.loadingCostCategories')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            {translate('pos.common.action.refresh')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.cost_categories.title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate('pos.cost_categories.subtitle')}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleCreateCategory}>
            <Plus className="h-4 w-4 mr-2" />
            {translate('pos.cost_categories.new_category')}
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder={translate(
                    'pos.cost_categories.search_placeholder'
                  )}
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 border rounded-md p-1">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="gap-2"
                >
                  <Grid3x3 className="h-4 w-4" />
                  {translate('pos.product_categories.view_grid', 'Table')}
                </Button>
                <Button
                  variant={viewMode === 'tree' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('tree')}
                  className="gap-2"
                >
                  <ListTree className="h-4 w-4" />
                  {translate('pos.product_categories.view_tree', 'Tree')}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilterModal(true)}
                className="border-border text-foreground hover:bg-muted"
              >
                <Filter className="h-4 w-4 mr-2" />
                {translate('pos.common.action.add_filter')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                }}
                className="border-border text-foreground hover:bg-muted"
              >
                {translate('common.filters')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {viewMode === 'table' && groupedCategories.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {translate('pos.cost_categories.no_results')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {translate('pos.cost_categories.try_adjusting_search')}
            </p>
            {canCreate && (
              <Button onClick={handleCreateCategory}>
                <Plus className="h-4 w-4 mr-2" />
                {translate('pos.cost_categories.add_first_category')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <div className="space-y-4">
          {/* Table View */}
          <Card>
            <CardContent className="p-0 relative">
              {tableLoading && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="text-center">
                    <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Loading...</p>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 font-medium">
                        {translate('pos.costCategories.fields.name')}
                      </th>
                      <th className="text-left p-4 font-medium">
                        {translate('pos.costCategories.fields.description')}
                      </th>
                      <th className="text-left p-4 font-medium">
                        {translate('pos.costCategories.fields.parent')}
                      </th>
                      <th className="text-left p-4 font-medium">
                        {translate('pos.costCategories.fields.isDefault')}
                      </th>
                      <th className="text-left p-4 font-medium">
                        {translate('pos.cost_categories.created')}
                      </th>
                      <th className="text-left p-4 font-medium">
                        {translate('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedCategories.map((group) => (
                      <React.Fragment key={group.parentId || 'no-parent'}>
                        {/* Group Header */}
                        <tr className="bg-muted/30 border-b-2 border-muted">
                          <td colSpan={6} className="p-3">
                            <div className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              <span className="font-semibold text-sm">
                                {group.parentName ||
                                  translate(
                                    'pos.cost_categories.no_parent',
                                    'No Parent Category'
                                  )}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({group.categories.length}{' '}
                                {group.categories.length === 1
                                  ? translate(
                                      'pos.cost_categories.category',
                                      'category'
                                    )
                                  : translate(
                                      'pos.cost_categories.categories',
                                      'categories'
                                    )}
                                )
                              </span>
                            </div>
                          </td>
                        </tr>
                        {/* Group Categories */}
                        {group.categories.map((category) => (
                          <tr
                            key={category.id}
                            className="border-b hover:bg-muted/50"
                          >
                            <td className="p-4">
                              <div className="font-medium pl-4">
                                {category.name}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm text-muted-foreground">
                                {category.description}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">
                                {getParentCategoryName(category.parent)}
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge
                                variant={
                                  category.isDefault ? 'default' : 'secondary'
                                }
                              >
                                {category.isDefault
                                  ? translate('pos.cost_categories.default')
                                  : translate('pos.cost_categories.custom')}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <div className="text-sm text-muted-foreground">
                                {formatDate(category.createdAt)}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {canEditCategory && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleEditCategory(category.id)
                                    }
                                    className="border-border text-foreground hover:bg-muted"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDeleteCategory && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteCategory(category.id)
                                    }
                                    className="border-border text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Tree View */
        <Card>
          <CardContent className="pt-6">
            {categoryTree.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm
                  ? translate('pos.cost_categories.no_results')
                  : translate('pos.cost_categories.no_categories', 'No categories found')}
              </div>
            ) : (
              <div className="space-y-1">
                {categoryTree.map((category) => (
                  <CostCategoryTreeNode
                    key={category.id}
                    category={category}
                    level={0}
                    expandedNodes={expandedNodes}
                    onToggle={toggleNode}
                    onEdit={canEditCategory ? handleEditCategory : undefined}
                    onDelete={canDeleteCategory ? handleDeleteCategory : undefined}
                    getParentCategoryName={getParentCategoryName}
                    formatDate={formatDate}
                    translate={translate}
                  />
                ))}
        </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-card-foreground">
                {translate('pos.cost_categories.filter_categories')}
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowFilterModal(false)}
              >
                ×
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  {translate('pos.cost_categories.default_status')}
                </Label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  aria-label={translate(
                    'pos.cost_categories.select_default_status'
                  )}
                >
                  <option value="">
                    {translate('pos.cost_categories.all_categories')}
                  </option>
                  <option value="default">
                    {translate('pos.cost_categories.default_categories')}
                  </option>
                  <option value="custom">
                    {translate('pos.cost_categories.custom_categories')}
                  </option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                }}
                className="flex-1"
              >
                {translate('pos.cost_categories.clear_all')}
              </Button>
              <Button
                onClick={() => setShowFilterModal(false)}
                className="flex-1"
              >
                {translate('pos.cost_categories.apply_filters')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination - Only show in table view */}
      {viewMode === 'table' && paginatedGroupedCategories.totalItems > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('common.page', 'Page')} {pagination.page}{' '}
              {translate('common.of', 'of')}{' '}
              {paginatedGroupedCategories.totalPages || 1} (
              {paginatedGroupedCategories.totalGroups}{' '}
              {translate('common.groups', 'groups')},{' '}
              {paginatedGroupedCategories.totalItems}{' '}
              {translate('common.total_items', 'total items')})
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.previous',
                  'Previous'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page >=
                  (paginatedGroupedCategories.totalPages || 1)
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.next',
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tree Node Component
interface CostCategoryTreeNodeProps {
  category: CostCategory & { children: CostCategory[] };
  level: number;
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  getParentCategoryName: (parentId: string | null | undefined) => string;
  formatDate: (date: Date | string | null | undefined) => string;
  translate: (key: string, fallback?: string) => string;
}

const CostCategoryTreeNode: React.FC<CostCategoryTreeNodeProps> = ({
  category,
  level,
  expandedNodes,
  onToggle,
  onEdit,
  onDelete,
  getParentCategoryName,
  formatDate,
  translate,
}) => {
  const hasChildren = category.children.length > 0;
  const isExpanded = expandedNodes.has(category.id);
  const indent = level * 24;

  return (
    <div>
      <div
        className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors group"
        style={{ paddingLeft: `${indent + 8}px` }}
      >
        {hasChildren ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onToggle(category.id)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div className="w-6" /> // Spacer for alignment
        )}
        <div className="flex-1 font-medium">{category.name}</div>
        {category.description && (
          <div className="text-sm text-muted-foreground max-w-xs truncate">
            {category.description}
          </div>
        )}
        {category.parent && (
          <Badge variant="outline" className="text-xs">
            {getParentCategoryName(category.parent)}
          </Badge>
        )}
        <Badge
          variant={category.isDefault ? 'default' : 'secondary'}
          className="text-xs"
        >
          {category.isDefault
            ? translate('pos.cost_categories.default')
            : translate('pos.cost_categories.custom')}
        </Badge>
        {category.createdAt && (
          <div className="text-xs text-muted-foreground">
            {formatDate(category.createdAt)}
          </div>
        )}
        {(onEdit || onDelete) && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onEdit(category.id)}
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={() => onDelete(category.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {category.children.map((child) => (
            <CostCategoryTreeNode
              key={child.id}
              category={child as CostCategory & { children: CostCategory[] }}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              getParentCategoryName={getParentCategoryName}
              formatDate={formatDate}
              translate={translate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModernCostCategoryList;
